import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, ImagePlus, MessageCircle } from "lucide-react";
import MessageThread from "@/components/shared/MessageThread";

const statusColor = {
    pending:   "bg-yellow-100 text-yellow-900 border-yellow-500",
    accepted:  "bg-blue-50 text-blue-900 border-blue-500",
    rejected:  "bg-red-50 text-red-900 border-red-500",
    converted: "bg-green-50 text-green-900 border-green-600",
};

export default function ClientDesignRequests() {
    const [requests, setRequests] = useState([]);
    const [openId, setOpenId] = useState(null);
    // form state
    const [title, setTitle] = useState("");
    const [qty, setQty] = useState(200);
    const [target, setTarget] = useState("");
    const [currency, setCurrency] = useState("GBP");
    const [notes, setNotes] = useState("");
    const [image, setImage] = useState(null); // {storage_path, url_preview}
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/design-requests");
            setRequests(r.data || []);
        } catch { /* silent */ }
    };
    useEffect(() => { load(); }, []);

    const handleImage = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setImage({ storage_path: r.data.storage_path, url_preview: fileUrl(r.data.storage_path) });
            toast.success("Design image uploaded");
        } catch (err) {
            toast.error("Upload failed");
        } finally { setUploading(false); }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim()) return toast.error("Give your design a title");
        if (parseInt(qty, 10) < 200) return toast.error("Minimum quantity is 200 pieces");
        if (!parseFloat(target) || parseFloat(target) <= 0) return toast.error("Enter a valid target price");
        setSubmitting(true);
        try {
            await api.post("/design-requests", {
                title: title.trim(),
                quantity: parseInt(qty, 10),
                target_price: parseFloat(target),
                currency,
                notes: notes.trim(),
                image_storage_path: image?.storage_path || "",
                image_url: image?.url_preview || "",
            });
            toast.success("Design submitted — A V Corp will review shortly");
            setTitle(""); setQty(200); setTarget(""); setNotes(""); setImage(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submission failed");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-10">
            <div className="mb-8">
                <div className="overline text-neutral-500">Bespoke</div>
                <h1 className="font-display text-5xl tracking-tight leading-none mt-2">Your Designs</h1>
                <p className="mt-3 text-sm font-body text-neutral-500 max-w-xl">Send us a picture of your own design plus quantity and target price. Our team reviews within 24h and turns it into a negotiable order.</p>
            </div>

            {/* Upload form */}
            <form onSubmit={submit} className="border border-black p-6 mb-10 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="design-submit-form">
                <div className="md:col-span-1">
                    <div className="overline text-neutral-500 mb-2">Design Image</div>
                    <label className="cursor-pointer block border-2 border-dashed border-black aspect-[3/4] flex items-center justify-center overflow-hidden bg-neutral-50 hover:bg-neutral-100 transition-colors">
                        {image?.url_preview ? (
                            <img src={image.url_preview} alt="preview" className="w-full h-full object-cover" data-testid="design-image-preview"/>
                        ) : (
                            <div className="text-center">
                                <ImagePlus className="h-8 w-8 mx-auto mb-2 text-neutral-500" strokeWidth={1.5}/>
                                <div className="uppercase tracking-widest text-xs text-neutral-600 font-semibold">
                                    {uploading ? "Uploading…" : "Click to upload"}
                                </div>
                                <div className="text-[10px] text-neutral-500 mt-1 font-body">JPG / PNG · up to 8MB</div>
                            </div>
                        )}
                        <input type="file" accept="image/*" className="hidden" onChange={handleImage} disabled={uploading} data-testid="design-image-input"/>
                    </label>
                </div>
                <div className="md:col-span-2 space-y-4">
                    <div>
                        <label className="overline text-neutral-500 block mb-1">Design Title</label>
                        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Draped satin midi in emerald" className="w-full border border-black h-10 px-3 rounded-none text-sm font-body" data-testid="design-title-input"/>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className="overline text-neutral-500 block mb-1">Quantity (pcs)</label>
                            <input type="number" min="200" step="50" value={qty} onChange={(e) => setQty(e.target.value)} className="w-full border border-black h-10 px-3 rounded-none text-sm font-mono tabular-nums" data-testid="design-qty-input"/>
                            <div className="text-[10px] text-neutral-500 mt-1 font-body">MOQ 200</div>
                        </div>
                        <div>
                            <label className="overline text-neutral-500 block mb-1">Target Price / pc</label>
                            <input type="number" min="0" step="0.01" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="0.00" className="w-full border border-black h-10 px-3 rounded-none text-sm font-mono tabular-nums" data-testid="design-target-input"/>
                        </div>
                        <div>
                            <label className="overline text-neutral-500 block mb-1">Currency</label>
                            <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="w-full border border-black h-10 px-2 rounded-none text-sm font-body bg-white" data-testid="design-currency-select">
                                <option>GBP</option><option>USD</option><option>EUR</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="overline text-neutral-500 block mb-1">Notes for A V Corp</label>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Fabric, colourways, sizing, delivery timeline…" className="w-full border border-black p-3 rounded-none text-sm font-body focus:outline-none" data-testid="design-notes-input"/>
                    </div>
                    <button type="submit" disabled={submitting || uploading} className="inline-flex items-center gap-2 h-11 px-5 bg-black text-white uppercase tracking-widest text-xs font-semibold hover:bg-neutral-800 disabled:opacity-40" data-testid="design-submit-btn">
                        <Upload className="h-4 w-4" strokeWidth={1.5}/> {submitting ? "Submitting…" : "Submit Design"}
                    </button>
                </div>
            </form>

            {/* History */}
            <div className="mb-4 flex items-baseline justify-between">
                <h2 className="font-display text-2xl tracking-tight">Your submissions</h2>
                <div className="text-xs font-body text-neutral-500">{requests.length} total</div>
            </div>

            {requests.length === 0 && (
                <div className="border border-dashed border-border p-10 text-center text-sm text-neutral-500 font-body">
                    No designs submitted yet.
                </div>
            )}

            <div className="space-y-3">
                {requests.map((d) => (
                    <div key={d.id} className="border border-border" data-testid={`design-row-${d.id}`}>
                        <div className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-center">
                            <div className="md:col-span-1 aspect-square bg-neutral-100 overflow-hidden">
                                {d.image_url && <img src={d.image_url} alt={d.title} className="w-full h-full object-cover"/>}
                            </div>
                            <div className="md:col-span-2">
                                <div className="font-mono text-[10px] text-neutral-500">{d.id.slice(0, 8)}</div>
                                <div className="font-body text-sm mt-1"><b>{d.title}</b></div>
                                <div className="text-[11px] text-neutral-500 mt-1">{new Date(d.created_at).toLocaleDateString()}</div>
                            </div>
                            <div>
                                <div className="overline text-neutral-500">Qty</div>
                                <div className="tabular-nums font-body text-sm">{d.quantity} pcs</div>
                            </div>
                            <div>
                                <div className="overline text-neutral-500">Target</div>
                                <div className="tabular-nums font-body text-sm">{d.currency === "GBP" ? "£" : d.currency === "USD" ? "$" : "€"}{d.target_price?.toFixed(2)}/pc</div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <span className={`inline-block px-2 py-1 border uppercase tracking-widest text-[10px] font-semibold text-center ${statusColor[d.status]}`} data-testid={`design-status-${d.id}`}>
                                    {d.status}
                                </span>
                                <button onClick={() => setOpenId(openId === d.id ? null : d.id)} className="text-xs uppercase tracking-widest text-neutral-600 hover:text-black inline-flex items-center gap-1 justify-center" data-testid={`toggle-design-${d.id}`}>
                                    <MessageCircle className="h-3 w-3" strokeWidth={1.5}/> {openId === d.id ? "Close" : "Chat"}
                                </button>
                            </div>
                        </div>
                        {openId === d.id && (
                            <div className="border-t border-border bg-neutral-50 p-4">
                                {d.admin_note && (
                                    <div className="mb-3 p-3 border border-black bg-white text-xs font-body">
                                        <div className="overline text-neutral-500 mb-1">A V Corp note</div>
                                        {d.admin_note}
                                    </div>
                                )}
                                {d.notes && (
                                    <div className="mb-3 p-3 border border-black/20 bg-white text-xs font-body">
                                        <div className="overline text-neutral-500 mb-1">Your notes</div>
                                        {d.notes}
                                    </div>
                                )}
                                <MessageThread threadType="design" threadId={d.id}/>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
